import cn from 'classnames';
import debounce from 'lodash.debounce';
import NextImage from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import Icon from '@components/ui/Icons';

import { useEventListener } from '@hooks/useEventListener';
import { Swatch } from '@hooks/useHitSwatches';

import styles from './Swatches.module.scss';

const Swatches = ({ swatches }: { swatches: Swatch[] }) => {
  const swatchRef = useRef<HTMLDivElement | null>(null);
  const [swatchOverflow, setSwatchOverflow] = useState(false);
  const [displayOverflow, setDisplayOverflow] = useState(false);

  const toggleOverflow = () => setDisplayOverflow((state) => !state);
  const handleOutsideClick = () => {
    setDisplayOverflow(false);
  };
  const handleOverflow = debounce(() => {
    if (!swatchRef.current) {
      return;
    }
    if (
      swatchRef.current.scrollHeight > swatchRef.current.clientHeight &&
      !swatchOverflow
    ) {
      setSwatchOverflow(true);
    } else if (
      swatchRef.current.scrollHeight <= swatchRef.current.clientHeight &&
      swatchOverflow
    ) {
      setSwatchOverflow(false);
    }
  }, 300);

  useEffect(() => {
    handleOverflow();
  }, [handleOverflow]);

  useEventListener('resize', handleOverflow);

  const handlePreload = (image: string | null) => {
    if (typeof image === 'string' && window !== undefined) {
      const img = new Image();
      img.src = image;
    }
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <div
        className={cn(styles.hitSwatches, {
          [styles.swatchesOpen]: displayOverflow,
        })}
        ref={swatchRef}
      >
        <div className={styles.swatchWrapper}>
          {swatches.length > 1 &&
            swatches.map(({ image, swatchName, swatch, isActive, onClick }) => {
              return (
                <button
                  key={swatchName}
                  className={cn(styles.swatchButton, {
                    [styles.swatchButtonActive]: isActive,
                    [styles.swatchButtonColourNotAvailable]: !swatch,
                  })}
                  onClick={onClick}
                  aria-pressed={isActive}
                  onMouseEnter={() => handlePreload(image)}
                  title={swatchName}
                >
                  {swatch ? (
                    <NextImage
                      src={swatch}
                      alt={swatchName}
                      height={16}
                      width={16}
                      className={styles.swatchImage}
                    />
                  ) : (
                    <span className={styles.swatchFallback}></span>
                  )}
                </button>
              );
            })}
        </div>
        {swatchOverflow && (
          <button className={styles.overflowToggle} onClick={toggleOverflow}>
            <Icon
              icon={displayOverflow ? 'caret-up' : 'caret-down'}
              height={16}
              width={16}
            />
          </button>
        )}
      </div>
    </OutsideClickHandler>
  );
};

export default Swatches;
